import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Card,
    Chip,
    Typography,
    type TypographyProps,
} from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    isFixableIssueKind,
    type IssueEntry,
    type IssueKind,
} from '../../importIssues';

/** The count of a kind with no default fix: its rows ("3 rows"), as the step's header counts them. */
const ROWS_COUNT = 'catalogImport.products.rows';

/** The line of a fix kind whose default changes none of its rows. */
const NO_FIX_LEFT = 'catalogImport.products.issues.noFixLeft';

/**
 * Each kind's copy (`pages.catalogImport.products.issues.kinds.<kind>`), as
 * literal keys so the locale guards see every one of them: its title, its
 * count, its fix (none for a kind with no default, nor for the extra
 * barcodes, which the import keeps) and its line (the default the fix
 * applies, or what the problem is and that it has no default).
 */
const KIND_COPY = {
    imei: {
        title: 'catalogImport.products.issues.kinds.imei.title',
        count: 'catalogImport.products.issues.kinds.imei.count',
        action: 'catalogImport.products.issues.kinds.imei.action',
        description: 'catalogImport.products.issues.kinds.imei.default',
    },
    variantFields: {
        title: 'catalogImport.products.issues.kinds.variantFields.title',
        count: 'catalogImport.products.issues.kinds.variantFields.count',
        action: 'catalogImport.products.issues.kinds.variantFields.action',
        description:
            'catalogImport.products.issues.kinds.variantFields.default',
    },
    groupFields: {
        title: 'catalogImport.products.issues.kinds.groupFields.title',
        count: 'catalogImport.products.issues.kinds.groupFields.count',
        action: 'catalogImport.products.issues.kinds.groupFields.action',
        description: 'catalogImport.products.issues.kinds.groupFields.default',
    },
    sharedBarcode: {
        title: 'catalogImport.products.issues.kinds.sharedBarcode.title',
        count: 'catalogImport.products.issues.kinds.sharedBarcode.count',
        action: 'catalogImport.products.issues.kinds.sharedBarcode.action',
        description:
            'catalogImport.products.issues.kinds.sharedBarcode.default',
    },
    fieldChecks: {
        title: 'catalogImport.products.issues.kinds.fieldChecks.title',
        count: ROWS_COUNT,
        action: null,
        description: 'catalogImport.products.issues.kinds.fieldChecks.about',
    },
    references: {
        title: 'catalogImport.products.issues.kinds.references.title',
        count: ROWS_COUNT,
        action: null,
        description: 'catalogImport.products.issues.kinds.references.about',
    },
    barcodeMatch: {
        title: 'catalogImport.products.issues.kinds.barcodeMatch.title',
        count: ROWS_COUNT,
        action: null,
        description: 'catalogImport.products.issues.kinds.barcodeMatch.about',
    },
    attributes: {
        title: 'catalogImport.products.issues.kinds.attributes.title',
        count: ROWS_COUNT,
        action: null,
        description: 'catalogImport.products.issues.kinds.attributes.about',
    },
    other: {
        title: 'catalogImport.products.issues.kinds.other.title',
        count: ROWS_COUNT,
        action: null,
        description: 'catalogImport.products.issues.kinds.other.about',
    },
    alternates: {
        title: 'catalogImport.products.issues.kinds.alternates.title',
        count: 'catalogImport.products.issues.kinds.alternates.count',
        action: null,
        description: 'catalogImport.products.issues.kinds.alternates.default',
    },
} as const satisfies Record<
    IssueKind,
    {
        title: string;
        count: string;
        action: string | null;
        description: string;
    }
>;

type IssueCardProps = {
    entry: IssueEntry;
    /** The table shows only this kind's rows. */
    isFiltered: boolean;
    /** The fix waits (a check or the import is running). */
    isFixDisabled: boolean;
    onApplyFix: (kind: IssueKind) => void;
    /** Shows only this kind's rows, or every row again with `null`. */
    onShowRows: (kind: IssueKind | null) => void;
};

/**
 * The fix's label can be longer than its card ("პირველი შევსებული
 * მნიშვნელობები 1 SKU-ს" is 357 px, a card on a 1,280 px laptop or a 375 px
 * phone holds 260 to 311): the button keeps to the card and grows to two
 * lines instead of widening the card and scrolling the page sideways.
 * Sizing only, the Button's own styles untouched: one line keeps the small
 * size's height. The label wraps inside its own `span` (`FIX_LABEL_WRAP`),
 * which takes the button's width as its limit.
 */
const FIX_BUTTON_WRAP = 'h-auto min-h-9 max-w-full py-1.5 md:min-h-8';
const FIX_LABEL_WRAP = 'min-w-0 whitespace-normal';

// A card's title is a heading under the summary's own.
const asH3: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <h3 {...domProps}>{children}</h3>;

/**
 * One kind of problem in the issues summary: a chip saying whether it
 * blocks the import ("Blocks import", danger) or the import does it on its
 * own ("Done for you", success, the extra barcodes), its count in the
 * kind's unit, its title and the default its fix applies, then the bulk fix
 * ("Move 778 IMEIs to Lot number", secondary) and **Show rows**, which
 * filters the table to the kind (and reads "Show all rows" while it does).
 * A kind with no default fix ("Cells to correct") blocks too: its count in
 * rows, what the problem is and that it has no default, and Show rows only.
 * So does a fix kind whose default changes none of its rows (`fix` null: a
 * variant that differs only by an unreadable cell).
 */
export const IssueCard: React.FC<IssueCardProps> = ({
    entry,
    isFiltered,
    isFixDisabled,
    onApplyFix,
    onShowRows,
}) => {
    const { t } = useTranslation('pages');
    const { kind, count } = entry;
    const copy = KIND_COPY[kind];
    const isNoFixLeft = isFixableIssueKind(kind) && entry.fix === null;
    const counted = { count, rows: countParam(count) };
    const isDoneForYou = kind === 'alternates';

    return (
        <Card
            className="min-w-0 flex-1 gap-3"
            data-kind={kind}
            {...getNodeTestIdProp(`catalog-import-issue-${kind}`)}
        >
            <Card.Header className="gap-1">
                <div className="flex flex-wrap items-center gap-2">
                    <Chip
                        color={isDoneForYou ? 'success' : 'danger'}
                        size="sm"
                        variant="soft"
                    >
                        {t(
                            isDoneForYou
                                ? 'catalogImport.products.issues.doneForYou'
                                : 'catalogImport.products.issues.blocks',
                        )}
                    </Chip>
                    <Typography color="muted" type="body-xs">
                        {t(isNoFixLeft ? ROWS_COUNT : copy.count, counted)}
                    </Typography>
                </div>
                <Card.Title render={asH3}>{t(copy.title)}</Card.Title>
                <Card.Description>
                    {t(isNoFixLeft ? NO_FIX_LEFT : copy.description)}
                </Card.Description>
            </Card.Header>
            <Card.Footer className="mt-auto flex-wrap gap-2">
                {copy.action === null || isNoFixLeft ? null : (
                    <Button
                        className={FIX_BUTTON_WRAP}
                        isDisabled={isFixDisabled}
                        onPress={() => onApplyFix(kind)}
                        size="sm"
                        variant="secondary"
                    >
                        <span className={FIX_LABEL_WRAP}>
                            {t(copy.action, counted)}
                        </span>
                    </Button>
                )}
                <Button
                    onPress={() => onShowRows(isFiltered ? null : kind)}
                    size="sm"
                    variant="ghost"
                >
                    {t(
                        isFiltered
                            ? 'catalogImport.products.issues.showAll'
                            : 'catalogImport.products.issues.showRows',
                    )}
                </Button>
            </Card.Footer>
        </Card>
    );
};
